import React, { useState } from 'react';
import BibliothequePlotly from '../../Graphe/AffichageGraphe/BibliothequePlotly/BibliothequePlotly';
import { mockCapteurs } from '../mockDonnees';
import S from '../GestionDonnees.module.scss';

const TYPES_GRAPHE = [
    { id: 'lines+markers', label: 'Lignes + Points' },
    { id: 'lines', label: 'Lignes' },
    { id: 'markers', label: 'Points' },
    { id: 'bar', label: 'Barres' },
];

const CorrectionTab = ({ capteurActif, setCapteurActif, donnees, onMettreAJour }) => {
    const [valeurEdit, setValeurEdit] = useState({});
    const [typeGraphe, setTypeGraphe] = useState('lines+markers');

    const capteurInfo = mockCapteurs.find((c) => c.id === capteurActif) || mockCapteurs[0];
    const anomalies = donnees.filter((d) => d.anomalie);

    const appliquerCorrection = (id) => {
        const novaValeur = parseFloat(valeurEdit[id]);
        if (isNaN(novaValeur)) return;
        onMettreAJour(
            donnees.map((d) =>
                d.id === id
                    ? {
                        ...d,
                        valeur_corrigee: novaValeur,
                        anomalie: false,
                        statut: 'ok',
                        commentaire: 'Corrigé manuellement',
                    }
                    : d
            )
        );
        setValeurEdit((prev) => {
            const n = { ...prev };
            delete n[id];
            return n;
        });
    };

    const annuler = () => {
        // Réinitialise uniquement les corrections non sauvegardées (via le parent)
        setValeurEdit({});
    };

    // Graphique unique — brutes (rouge) + corrigées (vert) sur le même axe temporel
    const dates = donnees.map((d) => d.date);
    const xRange = dates.length > 0 ? [dates[0], dates[dates.length - 1]] : undefined;

    // Série brutes : les points corrigés deviennent transparents, la ligne s'estompe quand tout est corrigé
    const toutCorrige = anomalies.length === 0;
    const couleurLigneBrute = toutCorrige ? 'rgba(231,76,60,0.18)' : '#e74c3c';

    const isBar = typeGraphe === 'bar';

    const plotData = [
        {
            x: dates,
            y: donnees.map((d) => d.valeur_brute),
            type: isBar ? 'bar' : 'scatter',
            mode: isBar ? undefined : typeGraphe,
            name: 'Brutes',
            ...(isBar
                ? { marker: { color: donnees.map((d) => (d.anomalie ? '#c0392b' : 'rgba(231,76,60,0.45)')) } }
                : {
                    line: { color: couleurLigneBrute, width: 2 },
                    marker: {
                        size: donnees.map((d) => (d.anomalie ? 9 : 4)),
                        color: donnees.map((d) => (d.anomalie ? '#c0392b' : 'rgba(231,76,60,0.15)')),
                        symbol: donnees.map((d) => (d.anomalie ? 'x' : 'circle')),
                    },
                }),
        },
        {
            x: dates,
            y: donnees.map((d) => d.valeur_corrigee),
            type: isBar ? 'bar' : 'scatter',
            mode: isBar ? undefined : typeGraphe + '+text',
            name: 'Corrigées',
            ...(isBar
                ? { marker: { color: '#27ae60' } }
                : {
                    line: { color: '#27ae60', width: 2 },
                    marker: { size: 4, color: '#27ae60' },
                }),
            text: donnees.map((d) => d.commentaire === 'Corrigé manuellement' ? 'Corrigé' : ''),
            textposition: 'top center',
            textfont: { size: 10, color: '#000000' },
        },
    ];

    const plotLayout = {
        margin: { l: 45, r: 10, t: 30, b: 45 },
        showlegend: true,
        legend: { orientation: 'h', y: -0.2 },
        xaxis: { type: 'date', range: xRange, title: 'Date / Heure' },
        yaxis: { title: capteurInfo.unite },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: '#fafafa',
        hovermode: 'x unified',
    };

    return (
        <>
            {/* Sélecteurs capteur + type de graphique */}
            <div className={S.correctionToolbar}>
                <select
                    className={S.capteurSelect}
                    value={capteurActif}
                    onChange={(e) => {
                        setValeurEdit({});
                        setCapteurActif(e.target.value);
                    }}
                >
                    {mockCapteurs.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.label}
                        </option>
                    ))}
                </select>
                <select
                    className={S.capteurSelect}
                    value={typeGraphe}
                    onChange={(e) => setTypeGraphe(e.target.value)}
                >
                    {TYPES_GRAPHE.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.label}
                        </option>
                    ))}
                </select>
                <span style={{ fontSize: 13, color: '#888' }}>
                    {anomalies.length} anomalie(s) restante(s)
                </span>
            </div>

            {/* Graphique fusionné — Brutes + Corrigées */}
            <div className={S.visualSection}>
                <div className={S.sectionHeader}>
                    <h3>Brutes vs Corrigées — {capteurInfo.label}</h3>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <span className={S.badgeErr}>{anomalies.length} anomalie(s)</span>
                        <span className={S.badgeOk}>{donnees.filter((d) => !d.anomalie).length} valide(s)</span>
                    </div>
                </div>
                <div className={S.plotContainer}>
                    <BibliothequePlotly
                        data={plotData}
                        layout={plotLayout}
                        dimensionWidth="100%"
                        dimensionHeight={260}
                        minimaliste
                    />
                </div>
            </div>

            {/* Tableau fusionné */}
            <div className={S.visualSection}>
                <div className={S.sectionHeader}>
                    <h3>Liste des mesures</h3>
                </div>
                <div className={S.tableWrapper}>
                    <table className={S.dataTable}>
                        <thead>
                            <tr>
                                <th>Date / Heure</th>
                                <th>Valeur brute</th>
                                <th>Statut</th>
                                <th>Valeur corrigée</th>
                                <th>Corriger</th>
                            </tr>
                        </thead>
                        <tbody>
                            {donnees.map((d) => (
                                <tr key={d.id} className={d.anomalie ? S.rowAnomalie : ''}>
                                    <td>{d.date.toLocaleString('fr-FR')}</td>
                                    <td>{d.valeur_brute} {capteurInfo.unite}</td>
                                    <td>
                                        {d.anomalie ? (
                                            <span className={S.badgeErr}>Erreur</span>
                                        ) : (
                                            <span className={S.badgeOk}>OK</span>
                                        )}
                                    </td>
                                    <td>{d.valeur_corrigee} {capteurInfo.unite}</td>
                                    <td>
                                        {d.anomalie ? (
                                            <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                <input
                                                    type="number"
                                                    className={S.inputCorrection}
                                                    step="0.1"
                                                    placeholder={d.valeur_corrigee}
                                                    value={valeurEdit[d.id] ?? ''}
                                                    onChange={(e) =>
                                                        setValeurEdit((prev) => ({ ...prev, [d.id]: e.target.value }))
                                                    }
                                                />
                                                <button
                                                    className={S.btnAppliquer}
                                                    onClick={() => appliquerCorrection(d.id)}
                                                >
                                                    ✓
                                                </button>
                                            </span>
                                        ) : (
                                            <span className={S.badgeOk}>OK</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Actions */}
            <div className={S.correctionActions}>
                <button
                    className={S.btnSauvegarder}
                    disabled={anomalies.length > 0}
                    title={anomalies.length > 0 ? 'Corrigez toutes les anomalies avant de sauvegarder' : ''}
                    onClick={() => {
                        // Point d'extension : appel API ici
                        console.info('Sauvegarde des corrections pour', capteurActif, donnees);
                    }}
                >
                    Sauvegarder les corrections
                </button>
                <button className={S.btnAnnuler} onClick={annuler}>
                    Annuler
                </button>
            </div>
        </>
    );
};

export default CorrectionTab;
